import { css } from '@emotion/react';

import { typography } from '@core/Typography2';
import generateEmotionClasses from '@core/utils/generateEmotionClasses';

import type { Props as LinkProps } from './Link';

export const classes = generateEmotionClasses('link', ['icon', 'focusVisible']);
export const getBeforeIconCss = css`
  margin-right: var(--cds-spacing-50);
`;
export const getAfterIconCss = css`
  margin-left: var(--cds-spacing-50);
`;

export const getLinkColor = (
  linkType: 'primary' | 'secondary',
  invert?: boolean,
  enableVisitedState?: boolean
) => {
  if (invert) {
    return 'color:var(--cds-color-neutral-primary-invert)';
  }
  if (enableVisitedState) {
    if (linkType === 'primary') {
      return 'color:var(--cds-color-interactive-primary-pressed)';
    }
    return 'color:var(--cds-color-interactive-secondary-pressed)';
  }
  if (linkType === 'primary') {
    return 'color:var(--cds-color-interactive-primary)';
  }
  return 'color:var(--cds-color-interactive-secondary)';
};

export const getHoverLinkColor = (
  linkType: 'primary' | 'secondary',
  invert?: boolean
) => {
  if (invert) {
    return 'color:var(--cds-color-neutral-primary-invert)';
  }
  if (linkType === 'primary') {
    return 'color:var(--cds-color-interactive-primary-hover)';
  }
  return 'color:var(--cds-color-interactive-secondary-hover)';
};

export const variants = {
  standard: (props: LinkProps) => css`
    ${getLinkColor('primary', props.invert, false)};
    text-decoration: underline;

    &:hover {
      ${getHoverLinkColor('primary', props.invert)};
      text-decoration: none;
    }
    &:visited:not(.${classes.focusVisible}) {
      ${getLinkColor('primary', props.invert, props.enableVisitedState)};
    }
    &.${classes.focusVisible} {
      ${getHoverLinkColor('primary', props.invert)};
    }
  `,
  quiet: (props: LinkProps) => css`
    ${getLinkColor('primary', props.invert, false)};
    text-decoration: none;

    &:hover {
      ${getHoverLinkColor('primary', props.invert)};
      text-decoration: underline;
    }
    &:visited:not(.${classes.focusVisible}) {
      ${getLinkColor('primary', props.invert, props.enableVisitedState)};
    }
    &.${classes.focusVisible} {
      ${getHoverLinkColor('primary', props.invert)};
    }
  `,
  standardSecondary: (props: LinkProps) => css`
    ${getLinkColor('secondary', props.invert, false)};
    text-decoration: underline;

    &:hover {
      ${getHoverLinkColor('secondary', props.invert)};
      text-decoration: none;
    }
    &:visited:not(.${classes.focusVisible}) {
      ${getLinkColor('secondary', props.invert, props.enableVisitedState)};
    }
    &.${classes.focusVisible} {
      ${getHoverLinkColor('secondary', props.invert)};
    }
  `,
  quietSecondary: (props: LinkProps) => css`
    ${getLinkColor('secondary', props.invert, false)};
    text-decoration: none;

    &:hover {
      ${getHoverLinkColor('secondary', props.invert)};
      text-decoration: underline;
    }
    &:visited:not(.${classes.focusVisible}) {
      ${getLinkColor('secondary', props.invert, props.enableVisitedState)};
    }
    &.${classes.focusVisible} {
      ${getHoverLinkColor('secondary', props.invert)};
    }
  `,
};

const getLinkCss = (props: LinkProps) => css`
  vertical-align: baseline;
  display: ${props.standalone
    ? props.iconPosition === 'before'
      ? 'inline-flex'
      : 'inline-block'
    : 'inline'};
  padding: ${props.standalone ? 'var(--cds-spacing-150) 0px' : 0};
  ${variants[props.variant || 'standard'](props)};
  .${classes.icon} {
    display: inline;

    > svg {
      display: inline-block;
      vertical-align: middle;

      /* Ensures the icon is vertically centered with the text for all supported typography variants. Using em units so it's relative to the font size */
      margin-top: -0.21em;
    }
  }

  &:focus {
    outline: none;
  }
  ${typography[props.typographyVariant as keyof typeof typography]};
  &.${classes.focusVisible} {
    outline: 1px solid transparent;
    outline-offset: 2px;
    border-radius: var(--cds-border-radius-50);
    text-decoration: underline;
    box-shadow: 0 0 0 1px var(--cds-color-interactive-stroke-primary-focus),
      0 0 0 2px var(--cds-color-interactive-stroke-primary-focus-invert);
  }
`;
export default getLinkCss;
